import React, { useMemo } from "react";
import { Box, Button, useMediaQuery } from "@mui/material";
import clsx from "clsx";
import FiltersArrowsSort from "./FiltersArrowsSort";
import { ClientSort, JourneyAnchors, TeamRole } from "../../constants";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import {
  CoachBriefInfoDto,
  TagDto,
} from "@growth-machine-llc/stridist-api-client";
import { useCurrentWorkspace } from "../../hooks/useCurrentWorkspace";
import {
  NONE_TAG_ID,
  TagsFilters,
} from "../exercise-library/filters/TagsFilters";
import {
  ALL_OPTIONS,
  ALL_OPTIONS_TYPE,
} from "../../wrappers/router/coach/CoachClientsWrapper";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { COACH_CLIENTS_PENDING_ROUTE } from "../../routes/routes";
import { CoachFilter } from "./CoachFilter";

const useStyles = makeStyles((theme) => ({
  button: {
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    fontWeight: 700,
    fontSize: 14,
    display: "flex",
    justifyContent: "flex-start",

    "&.active": {
      color: theme.palette.primary.main,
    },
  },
  buttonsContainer: {
    paddingLeft: theme.spacing(3),
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      justifyContent: () => "space-between",
      paddingLeft: 0,
    },
  },
  nameButton: {
    marginRight: "auto",
    [theme.breakpoints.down("md")]: {
      width: "auto",
      marginRight: 0,
    },
  },
  filterButtonContainer: {
    width: "17%",
    [theme.breakpoints.down("md")]: {
      width: "auto",
    },
    "&.active": {
      color: theme.palette.primary.main,
    },
    display: "flex",
    justifyContent: "center",
  },
  filterButton: {
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    fontWeight: 700,
    display: "flex",
    justifyContent: "flex-start",

    "&.active": {
      color: theme.palette.primary.main,
    },
  },
  coachButton: {
    width: "15%",
    [theme.breakpoints.down("md")]: {
      width: "auto",
    },
  },
  programButton: {
    marginRight: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },

  titleBlock: {
    display: "block",

    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },

  programProgress: {
    marginRight: theme.spacing(7),
    marginBottom: theme.spacing(2),
  },
  coachColumn: {
    width: 40,
    marginLeft: theme.spacing(1),
    marginRight: (disabled) => theme.spacing(disabled ? 41 : 3),
  },

  title: {
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    fontWeight: 700,
    fontSize: 14,
    display: "flex",
    justifyContent: "flex-start",
    margin: theme.spacing(1.2, 1),
    textWrap: "nowrap",
    whiteSpace: "nowrap",
  },

  description: {
    height: 0,
    marginTop: theme.spacing(-1.5),
    marginLeft: theme.spacing(1),
    fontSize: 12,
    fontWeight: 500,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.border.primary,
  },

  nutritionBlock: {
    marginRight: theme.spacing(13),
    marginBottom: theme.spacing(2),
  },
}));

type CoachClientsSortHeaderProps = {
  sortKey: string;
  handleChangeSortNameProgram: (value: boolean) => void;
  tagList: TagDto[];
  onChangeFilterTagId: (
    tagIds?: number[],
    allOptionsSelected?: boolean,
  ) => void;
  filterTagIds?: number[] | ALL_OPTIONS_TYPE;
  coachesList?: CoachBriefInfoDto[];
  onChangeFilterCoachId?: (
    coachIds?: number[],
    allOptionsSelected?: boolean,
  ) => void;
  filterCoachIds?: number[] | ALL_OPTIONS_TYPE;
  disabled: boolean;
};

const CoachClientsSortHeader = (props: CoachClientsSortHeaderProps) => {
  const {
    sortKey,
    handleChangeSortNameProgram,
    tagList,
    onChangeFilterTagId,
    filterTagIds,
    coachesList,
    onChangeFilterCoachId,
    filterCoachIds,
    disabled,
  } = props;
  const s = useStyles(disabled);

  const user = useCurrentUser();
  const isTeamAccountOwner =
    user?.teamRole === TeamRole.OWNER && user?.isTeamAccount;

  const workspace = useCurrentWorkspace();

  const activeFilters = useMemo(
    () => ({
      tag: filterTagIds !== null && filterTagIds != ALL_OPTIONS,
      coaches: filterCoachIds !== null && filterCoachIds != ALL_OPTIONS,
      name: [ClientSort.NAME_ASC, ClientSort.NAME_DESC].includes(
        sortKey as ClientSort,
      ),
      program: [
        ClientSort.PROGRAM_END_ASC,
        ClientSort.PROGRAM_END_DESC,
      ].includes(sortKey as ClientSort),
    }),
    [sortKey, filterTagIds, filterCoachIds],
  );

  const isAllTagsSelected = filterTagIds === ALL_OPTIONS;
  const allTagIds = (tagList ?? []).map((tag) => tag.id);
  const tagIds = isAllTagsSelected ? [...allTagIds, NONE_TAG_ID] : filterTagIds;

  const isAllCoachesSelected = filterCoachIds === ALL_OPTIONS;
  const allCoachIds = (coachesList ?? []).map((coach) => coach.id);
  const coachIds = isAllCoachesSelected ? [...allCoachIds] : filterCoachIds;

  return (
    <Box className={s.buttonsContainer}>
      <Box className={s.nameButton}>
        <Button
          className={clsx(s.button, {
            active: activeFilters.name,
          })}
          children="Name"
          endIcon={
            <FiltersArrowsSort
              activeUp={sortKey === ClientSort.NAME_ASC}
              activeDown={sortKey === ClientSort.NAME_DESC}
            />
          }
          onClick={() => handleChangeSortNameProgram(true)}
        />
      </Box>
      {!disabled && (
        <>
          {!location.pathname.includes(COACH_CLIENTS_PENDING_ROUTE) &&
            isTeamAccountOwner && (
              <Box
                className={clsx(s.filterButtonContainer, s.coachButton, {
                  active: activeFilters.coaches,
                })}
              >
                {coachesList?.length > 0 && coachIds?.length > 0 ? (
                  <CoachFilter
                    coaches={coachesList || []}
                    selectedCoachIds={coachIds}
                    onCoachesChange={onChangeFilterCoachId}
                    className={clsx(s.filterButton, {
                      active: activeFilters.coaches,
                    })}
                  />
                ) : (
                  <Button
                    className={clsx(s.filterButton)}
                    sx={{
                      pointerEvents: "none",
                    }}
                    children="Coaches"
                  />
                )}
              </Box>
            )}
          <Box
            className={clsx(s.filterButtonContainer, {
              active: activeFilters.tag,
            })}
          >
            {tagList?.length > 0 && tagIds?.length > 0 ? (
              <TagsFilters
                selectedTagIds={tagIds}
                onTagChange={onChangeFilterTagId}
                tags={tagList}
                className={clsx(s.filterButton, JourneyAnchors.CLIENTS_TAG, {
                  active: activeFilters.tag,
                })}
              />
            ) : (
              <Button
                className={clsx(s.filterButton)}
                sx={{
                  pointerEvents: "none",
                }}
                children="Tags"
              />
            )}
          </Box>

          <Box className={clsx(s.titleBlock, s.programButton)}>
            <Button
              className={clsx(s.button, {
                active: activeFilters.program,
              })}
              children={"program"}
              endIcon={
                <FiltersArrowsSort
                  activeUp={sortKey === ClientSort.PROGRAM_END_ASC}
                  activeDown={sortKey === ClientSort.PROGRAM_END_DESC}
                />
              }
              onClick={() => handleChangeSortNameProgram(false)}
            />
            <Box className={s.description}>closest due date</Box>
          </Box>

          <Box className={clsx(s.programProgress, s.titleBlock)}>
            <Box className={s.title}>program progress</Box>
            <Box className={s.description}>closest due date</Box>
          </Box>

          {workspace.nutritionsEnabled && (
            <Box className={clsx(s.titleBlock, s.nutritionBlock)}>
              <Box className={s.title}>nutrition</Box>
              <Box className={s.description}>last 7 days</Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default CoachClientsSortHeader;
